import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { AddConfirmToast } from './AddConfirmToast'
import { RemoveConfirmModal } from './RemoveConfirmModal'

export type DiscogsWriteAction =
  | 'add-collection'
  | 'add-wantlist'
  | 'remove-collection'
  | 'remove-wantlist'

export interface DiscogsWriteContextInfo {
  /** Release title or similar — for display in the prompt. */
  releaseLabel?: string
}

interface PendingPrompt {
  action: DiscogsWriteAction
  ctx: DiscogsWriteContextInfo
  resolve: (proceed: boolean) => void
}

interface DiscogsWriteGateValue {
  /**
   * Returns true if the user wants to proceed with the action. Shows a
   * teach-once confirmation the first time per action type; subsequent
   * calls (when the user has acknowledged) resolve immediately to true.
   */
  gate: (action: DiscogsWriteAction, ctx?: DiscogsWriteContextInfo) => Promise<boolean>
  /** Reset all teach flags — exposed for admin/debug. */
  resetTeaches: () => void
}

const Ctx = createContext<DiscogsWriteGateValue | null>(null)

const STORAGE_KEY = 'tenwords:discogs-write-teach-v1'

function loadTeaches(): Record<DiscogsWriteAction, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {} as Record<DiscogsWriteAction, boolean>
    return JSON.parse(raw)
  } catch {
    return {} as Record<DiscogsWriteAction, boolean>
  }
}

export function DiscogsWriteGateProvider({ children }: { children: ReactNode }) {
  const teachesRef = useRef<Record<DiscogsWriteAction, boolean>>({} as any)
  const [pending, setPending] = useState<PendingPrompt | null>(null)

  useEffect(() => {
    teachesRef.current = loadTeaches()
  }, [])

  const persist = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(teachesRef.current)) }
    catch { /* ignore */ }
  }, [])

  const gate = useCallback((
    action: DiscogsWriteAction,
    ctx: DiscogsWriteContextInfo = {},
  ): Promise<boolean> => {
    // If user already acknowledged this action type, proceed immediately
    if (teachesRef.current[action]) return Promise.resolve(true)
    return new Promise<boolean>((resolve) => {
      setPending({ action, ctx, resolve })
    })
  }, [])

  const handleResolve = useCallback((proceed: boolean, dontShowAgain: boolean) => {
    if (!pending) return
    if (proceed && dontShowAgain) {
      teachesRef.current = { ...teachesRef.current, [pending.action]: true }
      persist()
    }
    pending.resolve(proceed)
    setPending(null)
  }, [pending, persist])

  const resetTeaches = useCallback(() => {
    teachesRef.current = {} as Record<DiscogsWriteAction, boolean>
    persist()
  }, [persist])

  const isAdd = pending && (pending.action === 'add-collection' || pending.action === 'add-wantlist')
  const isRemove = pending && (pending.action === 'remove-collection' || pending.action === 'remove-wantlist')

  return (
    <Ctx.Provider value={{ gate, resetTeaches }}>
      {children}
      {pending && isAdd && (
        <AddConfirmToast
          action={pending.action as 'add-collection' | 'add-wantlist'}
          releaseLabel={pending.ctx.releaseLabel}
          onConfirm={(dontShowAgain) => handleResolve(true, dontShowAgain)}
          onCancel={() => handleResolve(false, false)}
        />
      )}
      {pending && isRemove && (
        <RemoveConfirmModal
          action={pending.action as 'remove-collection' | 'remove-wantlist'}
          releaseLabel={pending.ctx.releaseLabel}
          onConfirm={(dontShowAgain) => handleResolve(true, dontShowAgain)}
          onCancel={() => handleResolve(false, false)}
        />
      )}
    </Ctx.Provider>
  )
}

export function useDiscogsWriteGate(): DiscogsWriteGateValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useDiscogsWriteGate must be used inside DiscogsWriteGateProvider')
  return v
}
